import { RefObject, useEffect, useRef, useState } from 'react';

import { AnimationSettings, isIdentityAnimation, ViewportEntry } from './animation';

// TNBEWEB-1960 — on-load entrance animation.
//
// The editor's "Init" state (`style.states.init`) is the explicit "from"/origin
// snapshot the object animates OUT OF into its Base (root) style on page load.
// This hook drives that one-shot entrance by reusing the existing
// `active_state` → `states[state]` merge + the CSS `transition` that
// `generateComponentStyles` emits from `style.animation`:
//
//   1. First mount, if `states.init` carries a real (non-identity) animation:
//      render with `effectiveState = 'init'` (the origin) for the first frame.
//   2. After a double rAF, flip to the component's real `active_state` (usually
//      undefined) so the style settles to Base — the injected `entranceAnimation`
//      keeps a `transition` on the element so the change animates instead of
//      snapping.
//   3. `retain: 'start'` holds the origin (never flips), per the protocol.
//
// Absent / identity `states.init` → `effectiveState` is just `active_state` and
// `entranceAnimation` is undefined → Base renders statically (the fallback).
//
// TNBEWEB-1969 — viewport-entry trigger.
//   When `states.init.animation.viewport.enabled`, the mount-time double-rAF
//   flip is replaced by an IntersectionObserver on `targetRef`: the element
//   stays at its `init` origin (e.g. opacity/transparent) until it scrolls into
//   view, then flips to Base so the entrance plays exactly on appearance.
//   `threshold`/`rootMargin` parameterise the observer; `repeat` replays the
//   entrance on each re-entry (one-time + disconnect otherwise).
//   The observer's default root (the viewport) also clips against ancestor
//   scroll containers, so this works for elements inside an inner `overflow`
//   scroller, not just page scroll.
//   NOTE: the `init` origin must use opacity/transform/background, NOT
//   `display:none` — a display:none element has no box and is never observable.
export const useInitEntrance = (
  styleDraft: { states?: { init?: { animation?: AnimationSettings } }, animation?: AnimationSettings } | undefined,
  activeState: string | undefined,
  targetRef?: RefObject<HTMLElement | null>,
): { effectiveState: string | undefined, entranceAnimation: AnimationSettings | undefined } => {
  const initState = styleDraft?.states?.init;
  const entranceAnimation = initState?.animation || styleDraft?.animation;
  const hasEntrance = !!initState && !isIdentityAnimation(entranceAnimation);

  const viewport: ViewportEntry | undefined = initState?.animation?.viewport;
  const viewportEnabled = hasEntrance && viewport?.enabled === true;

  const [inOrigin, setInOrigin] = useState(hasEntrance);
  const playedRef = useRef(false);

  // Legacy mount-time entrance (double rAF). Skipped when the viewport trigger
  // owns the flip, so the two paths never both fire.
  useEffect(() => {
    if (!hasEntrance || viewportEnabled || playedRef.current) return undefined;
    playedRef.current = true;
    // retain:'start' → object stays at the origin; don't settle to Base.
    if (initState?.animation?.retain === 'start') return undefined;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setInOrigin(false));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // Entrance plays once per mount; intentionally only re-evaluates on hasEntrance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEntrance, viewportEnabled]);

  // Viewport-entry trigger (TNBEWEB-1969). Flips into Base on first/each entry.
  useEffect(() => {
    if (!viewportEnabled) return undefined;

    const retainStart = initState?.animation?.retain === 'start';

    // No ref passed AT ALL → we can never observe a node, so don't strand the
    // entrance at the (hidden) origin; play it immediately. Distinct from
    // "ref passed but node not mounted yet" (handled by the rAF retry below).
    if (!targetRef) {
      if (!retainStart) setInOrigin(false);
      return undefined;
    }

    const repeat = viewport?.repeat === true;

    let observer: IntersectionObserver | null = null;
    let rafId = 0;
    let attempts = 0;
    // ~5s at 60fps. A node behind a skeleton/loading placeholder mounts well
    // within this; the bound just prevents an unbounded rAF spin.
    const MAX_ATTEMPTS = 300;

    const attach = () => {
      const el = targetRef.current;
      if (!el) {
        // Ref passed but the node isn't in the DOM yet (a skeleton/loading
        // placeholder renders first). React won't re-run this effect when the
        // ref's .current later mutates, so STAY at the origin and retry next
        // frame rather than flipping to Base off-screen.
        attempts += 1;
        if (attempts > MAX_ATTEMPTS) {
          if (!retainStart) setInOrigin(false); // last resort: reveal, don't stay invisible
          return;
        }
        rafId = requestAnimationFrame(attach);
        return;
      }
      const obs = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) {
            // Play the entrance (settle to Base) unless the origin is retained.
            if (!retainStart) setInOrigin(false);
            if (!repeat) obs.disconnect();
          } else if (repeat && !retainStart) {
            // Reset to the origin while off-screen so the next entry replays.
            setInOrigin(true);
          }
        },
        {
          threshold: viewport?.threshold ?? 0.2,
          rootMargin: viewport?.rootMargin ?? '0px',
        },
      );
      observer = obs;
      obs.observe(el);
    };
    attach();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (observer) observer.disconnect();
    };
    // Re-arm only when the trigger config or the node changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportEnabled, viewport?.repeat, viewport?.threshold, viewport?.rootMargin, targetRef]);

  return {
    effectiveState: inOrigin ? 'init' : activeState,
    entranceAnimation: hasEntrance ? entranceAnimation : undefined,
  };
};
